import React, { useState } from 'react';
import { Clock, Edit2, Save, X, AlertCircle } from 'lucide-react';

const surveillanceColors = { 
  vert: "#22c55e", 
  jaune: "#facc15", 
  orange: "#fb923c", 
  rouge: "#ef4444" 
};

const SurveillanceStatus = ({ 
  currentState = 'vert', 
  currentMessage = '', 
  onStateChange,
  onShowHistory,
  isEditing = false,
  disabled = false
}) => {
  const [editMode, setEditMode] = useState(false);
  const [tempState, setTempState] = useState(currentState);
  const [tempMessage, setTempMessage] = useState(currentMessage || '');
  
  const states = [
    { value: 'vert', label: 'Normal', color: '#22c55e' },
    { value: 'jaune', label: 'Attention', color: '#facc15' },
    { value: 'orange', label: 'Vigilance', color: '#fb923c' },
    { value: 'rouge', label: 'Critique', color: '#ef4444' }
  ];

  const handleStartEdit = () => {
    setEditMode(true);
    setTempState(currentState);
    setTempMessage(currentMessage || '');
  };

  const handleSave = () => {
    // Sauvegarder seulement si quelque chose a changé
    if (tempState !== currentState || tempMessage !== currentMessage) {
      onStateChange(tempState, tempMessage);
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    setTempState(currentState);
    setTempMessage(currentMessage || '');
  };

  const handleStateClick = (newState) => {
    if (editMode) {
      setTempState(newState);
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          État de surveillance
        </h3>
        <div className="flex items-center gap-2">
          <button
  onClick={onShowHistory}
  className="text-sm text-gray-800 hover:text-gray-900 flex items-center gap-1"
  type="button"
>
  <Clock className="h-4 w-4 text-gray-800" />
  Historique
</button>
          {!editMode && !disabled && (
            <button
              onClick={handleStartEdit}
              className="p-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
              type="button"
              title="Modifier l'état de surveillance"
            >
              <Edit2 className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
      
      {/* Gélules - cliquables seulement en mode édition */}
      <div className="flex gap-2 mb-3">
        {states.map(state => (
          <button
            key={state.value}
            onClick={() => handleStateClick(state.value)}
            disabled={!editMode}
            className={`px-3 py-1.5 rounded-full text-white text-sm font-medium transition-all ${
              (editMode ? tempState : currentState) === state.value 
                ? 'ring-2 ring-offset-2 ring-gray-700 scale-105' 
                : editMode
                  ? 'hover:scale-105 cursor-pointer'
                  : 'cursor-not-allowed opacity-70'
            }`}
            style={{ backgroundColor: state.color }}
            type="button"
          >
            {state.label}
          </button>
        ))}
      </div>

      {/* Message - éditable seulement en mode édition */}
      <div className="mt-3">
        <label className="text-xs text-gray-600 mb-1 block">Message de surveillance:</label>
        {editMode ? (
          <div className="space-y-3">
            <textarea
              value={tempMessage}
              onChange={(e) => setTempMessage(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Message de surveillance (optionnel)..."
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                type="button"
              >
                <Save className="h-4 w-4" />
                Enregistrer
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                type="button"
              >
                <X className="h-4 w-4" />
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <div>
            {currentMessage ? (
              <p className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                {currentMessage}
              </p>
            ) : (
              <p className="text-sm text-gray-500 italic">Aucun message</p>
            )}
          </div>
        )}
      </div>

      {/* Indicateur d'état critique */}
      {(editMode ? tempState : currentState) === 'rouge' && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
          <p className="text-xs text-red-700 font-medium">
            ⚠️ État critique - Intervention urgente requise
          </p>
        </div>
      )}

      {/* Indicateur de mode édition */}
      {editMode && (
        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
          <p className="text-xs text-blue-700">
            Mode édition actif - Modifiez l'état et/ou le message puis enregistrez
          </p>
        </div>
      )}
    </div>
  );
};

export default SurveillanceStatus;